/**
 * PerformanceLens — Phase 2.
 *
 * Brief-scoped performance surface:
 *   - KPI strip (Spend, ROAS, Active Ads, Ad Packs) with honest empty markers.
 *   - Spend × ROAS daily time series (Recharts ComposedChart).
 *   - Angle leaderboard table (sortable on the client).
 *   - Linked ad-pack list (preserved from Phase 0 stub).
 *
 * Wiring: GET /api/v1/generation/briefs/{brief_id}/performance.
 * See docs/creative-briefs/requirements/2_performance_lens/.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Loader2 } from 'lucide-react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  generationApi,
  type BriefPerformanceAngleRow,
  type BriefPerformanceResponse,
  type BriefPerformanceWindow,
  type GenerationBrief,
} from '@/services/api';
import { useGenerationStore } from '@/stores/generation';
import type { LensKey } from '../constants';

interface Props {
  brief: GenerationBrief;
  /** Optional callback so the header's "X connected ads →" link can
   * jump to the Connected Ads lens without depending on URL state. */
  onSwitchLens?: (lens: LensKey) => void;
}

const WINDOWS: { key: BriefPerformanceWindow; label: string }[] = [
  { key: '7d', label: '7d' },
  { key: '14d', label: '14d' },
  { key: '30d', label: '30d' },
  { key: '90d', label: '90d' },
];

export function PerformanceLens({ brief, onSwitchLens }: Props) {
  const [window, setWindow] = useState<BriefPerformanceWindow>('30d');
  const [data, setData] = useState<BriefPerformanceResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    generationApi
      .getBriefPerformance(brief.brief_id, { window })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setError(message);
        setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [brief.brief_id, window]);

  const playbookCount = brief.playbook_ids?.length ?? 0;

  return (
    <div className="p-6 space-y-6">
      <Header
        window={window}
        onChangeWindow={setWindow}
        loading={loading}
        linkedCreatives={data?.linked_creatives ?? 0}
        onOpenConnectedAds={() => onSwitchLens?.('connected_ads')}
      />
      <KpiStrip data={data} loading={loading} fallbackPackCount={playbookCount} />
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-xs text-red-700">
          Performance data failed to load: {error}
        </div>
      )}
      <SpendRoasChart data={data} loading={loading} />
      <AngleLeaderboard angles={data?.angles ?? []} loading={loading && !data} />
      <PackList brief={brief} />
    </div>
  );
}

// =============================================================================
// Header
// =============================================================================

function Header({
  window,
  onChangeWindow,
  loading,
  linkedCreatives,
  onOpenConnectedAds,
}: {
  window: BriefPerformanceWindow;
  onChangeWindow: (w: BriefPerformanceWindow) => void;
  loading: boolean;
  linkedCreatives: number;
  onOpenConnectedAds: () => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <h2 className="text-sm font-bold text-slate-800">Performance</h2>
        <p className="text-[11px] text-slate-500 mt-0.5">
          Brief-scoped Spend × ROAS for ads tied to this thesis.
          {linkedCreatives > 0 && (
            <>
              {' · '}
              <button
                onClick={onOpenConnectedAds}
                className="text-brand-purple hover:underline"
              >
                {linkedCreatives} ad{linkedCreatives === 1 ? '' : 's'} →
              </button>
            </>
          )}
        </p>
      </div>
      <div className="flex items-center gap-3">
        {loading && <Loader2 size={12} className="animate-spin text-slate-400" />}
        <div className="inline-flex border border-slate-200 rounded-lg overflow-hidden">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              onClick={() => onChangeWindow(w.key)}
              className={
                'px-2.5 py-1 text-[10px] font-mono uppercase transition-colors ' +
                (window === w.key
                  ? 'bg-slate-900 text-white'
                  : 'bg-white text-slate-500 hover:bg-slate-50')
              }
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// KPI strip
// =============================================================================

function KpiStrip({
  data,
  loading,
  fallbackPackCount,
}: {
  data: BriefPerformanceResponse | null;
  loading: boolean;
  fallbackPackCount: number;
}) {
  const k = data?.kpis;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Kpi
        label="Spend"
        value={k?.spend ? formatCurrency(k.spend.value) : null}
        loading={loading && !data}
        hint={data ? `${data.window.days}d window` : undefined}
      />
      <Kpi
        label="ROAS"
        value={k?.roas ? formatRoas(k.roas.value) : null}
        loading={loading && !data}
        hint={k?.roas?.available === false ? 'no revenue yet' : undefined}
      />
      <Kpi
        label="Active ads"
        value={k?.active_ads ? formatInt(k.active_ads.value) : null}
        loading={loading && !data}
        hint={data ? `${data.linked_creatives} linked` : undefined}
      />
      <Kpi
        label="Ad packs"
        value={String(k?.ad_packs?.value ?? fallbackPackCount)}
        loading={false}
      />
    </div>
  );
}

function Kpi({
  label,
  value,
  loading,
  hint,
}: {
  label: string;
  value: string | null;
  loading?: boolean;
  hint?: string;
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
        {label}
      </div>
      <div className="text-2xl font-bold text-slate-900 mt-1">
        {loading ? <span className="text-slate-300">…</span> : value ?? '—'}
      </div>
      {hint && <div className="text-[10px] text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}

// =============================================================================
// Spend × ROAS chart
// =============================================================================

function SpendRoasChart({
  data,
  loading,
}: {
  data: BriefPerformanceResponse | null;
  loading: boolean;
}) {
  const points = data?.time_series.points ?? [];
  const hasSpend = useMemo(() => points.some((p) => p.spend > 0), [points]);
  const hasRoas = useMemo(
    () => points.some((p) => p.roas !== null && p.roas > 0),
    [points],
  );

  if (loading && !data) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6 text-center">
        <Loader2 size={16} className="animate-spin mx-auto text-slate-400" />
        <p className="text-[11px] text-slate-400 mt-2">Loading performance…</p>
      </div>
    );
  }

  if (!hasSpend) {
    return (
      <div className="bg-slate-50 border border-dashed border-slate-200 rounded-xl px-6 py-8 text-center">
        <div className="text-sm font-semibold text-slate-600">
          Spend × ROAS chart
        </div>
        <div className="text-[11px] text-slate-400 mt-1">
          {data?.empty_state.reason
            ? friendlyEmptyReason(data.empty_state.reason)
            : 'No spend recorded for the linked creatives in this window yet.'}
        </div>
      </div>
    );
  }

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-4">
      <header className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-bold text-slate-800">Spend × ROAS</h3>
          <p className="text-[10px] text-slate-400 mt-0.5">
            Daily spend bars × ROAS line
            {!hasRoas && ' · ROAS pending revenue ingest'}
          </p>
        </div>
      </header>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={points}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="date" stroke="#94a3b8" fontSize={10} />
            <YAxis
              yAxisId="spend"
              stroke="#94a3b8"
              fontSize={10}
              tickFormatter={(v) => formatCurrencyCompact(v as number)}
            />
            <YAxis
              yAxisId="roas"
              orientation="right"
              stroke="#0ea5e9"
              fontSize={10}
              tickFormatter={(v) => `${(v as number).toFixed(1)}x`}
            />
            <Tooltip
              wrapperClassName="text-xs"
              contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }}
              formatter={(value: number, name: string) => {
                if (name === 'ROAS') return [`${value.toFixed(2)}x`, name];
                return [formatCurrency(value), name];
              }}
            />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Bar
              yAxisId="spend"
              dataKey="spend"
              name="Spend"
              fill="#14b8a6"
              fillOpacity={0.85}
              radius={[2, 2, 0, 0]}
            />
            {hasRoas && (
              <Line
                yAxisId="roas"
                type="monotone"
                dataKey="roas"
                name="ROAS"
                stroke="#0ea5e9"
                strokeWidth={2}
                dot={false}
                connectNulls
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

// =============================================================================
// Angle leaderboard
// =============================================================================

type AngleSortKey = 'spend' | 'roas' | 'ads' | 'ctr';

function AngleLeaderboard({
  angles,
  loading,
}: {
  angles: BriefPerformanceAngleRow[];
  loading: boolean;
}) {
  const [sortKey, setSortKey] = useState<AngleSortKey>('spend');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');

  const sorted = useMemo(() => {
    const rows = [...angles];
    rows.sort((a, b) => {
      const av = pickSortValue(a, sortKey);
      const bv = pickSortValue(b, sortKey);
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      return sortDir === 'desc' ? bv - av : av - bv;
    });
    return rows;
  }, [angles, sortKey, sortDir]);

  if (!loading && angles.length === 0) {
    return (
      <div className="bg-slate-50 border border-dashed border-slate-200 rounded-xl px-6 py-8 text-center">
        <div className="text-sm font-semibold text-slate-600">Angle leaderboard</div>
        <div className="text-[11px] text-slate-400 mt-1">
          Approve angles on the Angles tab to populate this table.
        </div>
      </div>
    );
  }

  const handleSort = (key: AngleSortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-xl">
      <header className="px-4 py-3 border-b border-slate-100">
        <h3 className="text-sm font-bold text-slate-800">Angle leaderboard</h3>
        <p className="text-[10px] text-slate-400 mt-0.5">
          Per-angle spend / ROAS for the active window. Sorted by{' '}
          <span className="font-mono text-slate-600">{sortKey}</span> ({sortDir}).
        </p>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Hook</th>
              <th className="text-left px-4 py-2 font-semibold">Status</th>
              <SortHeader
                label="Ads"
                k="ads"
                active={sortKey === 'ads'}
                dir={sortDir}
                onClick={handleSort}
              />
              <SortHeader
                label="Spend"
                k="spend"
                active={sortKey === 'spend'}
                dir={sortDir}
                onClick={handleSort}
              />
              <SortHeader
                label="ROAS"
                k="roas"
                active={sortKey === 'roas'}
                dir={sortDir}
                onClick={handleSort}
              />
              <SortHeader
                label="CTR"
                k="ctr"
                active={sortKey === 'ctr'}
                dir={sortDir}
                onClick={handleSort}
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sorted.map((row) => (
              <tr key={row.angle_id} className="hover:bg-slate-50">
                <td className="px-4 py-2 max-w-[28rem]">
                  <div className="text-slate-800 font-medium truncate">
                    {row.hook || row.angle_id}
                  </div>
                  {!row.data_available && (
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      no creatives linked yet
                    </div>
                  )}
                </td>
                <td className="px-4 py-2">
                  <StatusPill status={row.status} />
                </td>
                <td className="px-4 py-2 font-mono text-slate-700">{row.ads}</td>
                <td className="px-4 py-2 font-mono text-slate-700">
                  {row.spend > 0 ? formatCurrency(row.spend) : '—'}
                </td>
                <td className="px-4 py-2 font-mono text-slate-700">
                  {row.roas !== null ? `${row.roas.toFixed(2)}x` : '—'}
                </td>
                <td className="px-4 py-2 font-mono text-slate-700">
                  {row.ctr !== null ? `${(row.ctr * 100).toFixed(2)}%` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SortHeader({
  label,
  k,
  active,
  dir,
  onClick,
}: {
  label: string;
  k: AngleSortKey;
  active: boolean;
  dir: 'asc' | 'desc';
  onClick: (k: AngleSortKey) => void;
}) {
  return (
    <th className="text-left px-4 py-2 font-semibold">
      <button
        type="button"
        onClick={() => onClick(k)}
        className={
          'inline-flex items-center gap-1 ' +
          (active ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700')
        }
      >
        {label}
        {active && <span aria-hidden>{dir === 'desc' ? '▾' : '▴'}</span>}
      </button>
    </th>
  );
}

function StatusPill({ status }: { status: string }) {
  const className =
    status === 'approved' || status === 'active'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
      : status === 'suggested'
        ? 'bg-slate-50 text-slate-600 border-slate-200'
        : status === 'rejected'
          ? 'bg-rose-50 text-rose-600 border-rose-100'
          : 'bg-slate-50 text-slate-500 border-slate-200';
  return (
    <span
      className={
        'inline-block text-[10px] uppercase tracking-wider font-semibold border rounded px-1.5 py-0.5 ' +
        className
      }
    >
      {status}
    </span>
  );
}

function pickSortValue(row: BriefPerformanceAngleRow, key: AngleSortKey): number | null {
  if (key === 'spend') return row.spend;
  if (key === 'ads') return row.ads;
  if (key === 'roas') return row.roas;
  if (key === 'ctr') return row.ctr;
  return null;
}

// =============================================================================
// Pack list (preserved from Phase 0 — still useful as quick navigation)
// =============================================================================

function PackList({ brief }: { brief: GenerationBrief }) {
  const navigate = useNavigate();
  const { campaignDetails, fetchCampaignDetail } = useGenerationStore();
  const playbookIds = brief.playbook_ids || [];

  useEffect(() => {
    for (const pid of playbookIds) {
      if (!campaignDetails[pid]) fetchCampaignDetail(pid);
    }
  }, [playbookIds, campaignDetails, fetchCampaignDetail]);

  if (playbookIds.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6 text-center">
        <p className="text-sm text-slate-500">No ad packs linked to this brief yet.</p>
        <p className="text-[11px] text-slate-400 mt-1">
          Generate one from the Production lens to start collecting performance.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl">
      <div className="px-4 py-3 border-b border-slate-100">
        <h3 className="text-sm font-bold text-slate-800">Linked ad packs</h3>
      </div>
      <div className="divide-y divide-slate-100">
        {playbookIds.map((pid) => {
          const detail = campaignDetails[pid];
          return (
            <button
              key={pid}
              onClick={() => navigate(`/generation/${pid}`)}
              className="w-full flex items-center justify-between gap-4 px-4 py-3 hover:bg-slate-50 transition text-left"
            >
              <div className="min-w-0 flex-1">
                <div className="font-mono text-[11px] text-slate-700 truncate">{pid}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  {detail
                    ? `${detail.status} · ${detail.asset_summary?.total_static_ads ?? 0} ads`
                    : 'Loading…'}
                </div>
              </div>
              <ChevronRight size={14} className="text-slate-400" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// Formatters
// =============================================================================

function formatCurrency(value: number | null): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatCurrencyCompact(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}k`;
  return `$${value.toFixed(0)}`;
}

function formatRoas(value: number | null): string {
  if (value === null || value === undefined) return '—';
  return `${value.toFixed(2)}x`;
}

function formatInt(value: number | null): string {
  if (value === null || value === undefined) return '—';
  return Math.round(value).toLocaleString();
}

function friendlyEmptyReason(reason: NonNullable<
  BriefPerformanceResponse['empty_state']['reason']
>): string {
  switch (reason) {
    case 'no_playbooks_linked':
      return 'No ad packs linked yet — generate one from Production to start collecting.';
    case 'no_creatives_for_brief':
      return 'Ad packs are linked, but no creatives have been ingested for this brief yet.';
    case 'no_performance_data_yet':
      return 'Creatives are linked but no platform spend has been reported yet.';
    default:
      return '';
  }
}
